"use client";

/**
 * Выбор эмодзи для чата: недавние, разделы, сетка.
 *
 * Раньше в поле ввода лежали шесть значков подряд — «остальное наберут с
 * клавиатуры». На деле с клавиатуры их никто не набирает (Максим, 04.09.2026:
 * «набор смайликов скудный»). Здесь ~330 значков по разделам и память о
 * недавних — этого хватает рабочему чату, а вес нулевой: рисует системный шрифт.
 */

import { useEffect, useRef, useState } from "react";
import { ГРУППЫ_ЭМОДЗИ, недавниеЭмодзи, запомнитьЭмодзи } from "@/lib/emoji";

export function EmojiPicker({ onPick, onClose }: { onPick: (символ: string) => void; onClose: () => void }) {
  const [раздел, setРаздел] = useState(ГРУППЫ_ЭМОДЗИ[0].ключ);
  const [недавние, setНедавние] = useState<string[]>([]);
  const корень = useRef<HTMLDivElement>(null);

  // Недавние читаем на клиенте: на сервере localStorage нет, а разметка должна совпасть.
  useEffect(() => setНедавние(недавниеЭмодзи()), []);

  // Закрытие по клику мимо и по Escape — как у остальных всплывашек портала.
  useEffect(() => {
    function мимо(e: MouseEvent) {
      if (корень.current && !корень.current.contains(e.target as Node)) onClose();
    }
    function клавиша(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    document.addEventListener("mousedown", мимо);
    document.addEventListener("keydown", клавиша);
    return () => { document.removeEventListener("mousedown", мимо); document.removeEventListener("keydown", клавиша); };
  }, [onClose]);

  const группа = ГРУППЫ_ЭМОДЗИ.find((г) => г.ключ === раздел) ?? ГРУППЫ_ЭМОДЗИ[0];

  function выбрать(символ: string) {
    запомнитьЭмодзи(символ);
    setНедавние(недавниеЭмодзи());
    onPick(символ);
  }

  const значок = "grid h-7 w-7 place-items-center rounded-[var(--radius-md)] text-[18px] leading-none transition data-row";

  return (
    <div ref={корень}
      className="absolute bottom-11 left-0 z-20 w-[292px] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-2 elev-pop"
      onMouseDown={(e) => e.stopPropagation()}>

      {недавние.length > 0 && (
        <>
          <div className="mb-1 px-0.5"><span className="field-label">Недавние</span></div>
          <div className="mb-2 flex flex-wrap gap-0.5">
            {недавние.slice(0, 16).map((с, i) => (
              <button key={`${с}-${i}`} type="button" onClick={() => выбрать(с)} className={значок}>{с}</button>
            ))}
          </div>
        </>
      )}

      {/* Разделы: значок вместо подписи — иначе ряд не влезает в ширину панели. */}
      <div className="mb-1.5 flex items-center gap-0.5 border-b border-[var(--border)] pb-1.5">
        {ГРУППЫ_ЭМОДЗИ.map((г) => (
          <button key={г.ключ} type="button" onClick={() => setРаздел(г.ключ)} data-tooltip={г.подпись}
            className={`grid h-7 w-7 place-items-center rounded-[var(--radius-md)] text-[15px] leading-none transition ${
              г.ключ === раздел ? "seg seg-on" : "seg"}`}>
            {г.значок}
          </button>
        ))}
      </div>

      <div className="mb-1 px-0.5"><span className="field-label">{группа.подпись}</span></div>
      {/* Высота фиксированная: иначе панель прыгает при смене раздела. */}
      <div className="flex h-[168px] flex-wrap content-start gap-0.5 overflow-y-auto">
        {группа.символы.map((с, i) => (
          <button key={`${с}-${i}`} type="button" onClick={() => выбрать(с)} className={значок}>{с}</button>
        ))}
      </div>
    </div>
  );
}
